/**
 * Texto livre: o que se escreve no editor de texto do site.
 *
 * É o "Docs" da plataforma, e ele é deliberadamente **sem estrutura**: não há
 * seção numerada, norma, referência nem sumário. Quem quer trabalho acadêmico usa
 * o [[Editor de documentos]]; aqui é carta, ata, receita, aula, roteiro, memorial,
 * lista de compras.
 *
 * Três decisões que valem para as duas telas (escrever e ler):
 *
 * 1. **A formatação do texto mora no HTML** (`negrito`, `alinhamento`, `tabela`),
 *    e o que é da folha — fonte, corpo, largura — mora em `FormatoDoTexto`. É essa
 *    separação que permite a tela de apresentação **aumentar o texto** sem tocar
 *    no que a pessoa escreveu.
 * 2. **Fonte só entre as que existem no computador de quem lê.** Nada de fonte
 *    baixada da internet: a folha que abre no celular do avô tem de ser a mesma do
 *    computador da neta.
 * 3. **O limite é de caracteres, e é generoso** — o guarda de verdade é o tamanho
 *    do corpo da requisição. Sem limite, um Ctrl+V de um livro inteiro derruba o
 *    banco.
 */
/* ------------------------------------------------------------------ */
/* Fontes                                                              */
/* ------------------------------------------------------------------ */
export const FONTES_DO_TEXTO = ['ARIAL', 'VERDANA', 'GEORGIA', 'TIMES'];
export const ROTULO_FONTE_DO_TEXTO = {
    ARIAL: 'Arial (sem serifa)',
    VERDANA: 'Verdana (letras largas)',
    GEORGIA: 'Georgia (com serifa)',
    TIMES: 'Times New Roman (clássica)',
};
export const DESCRICAO_FONTE_DO_TEXTO = {
    ARIAL: 'A mais comum: limpa e fina, funciona em qualquer tela.',
    VERDANA: 'Letras largas e espaçadas — a mais fácil de ler para quem enxerga pouco.',
    GEORGIA: 'Com serifa e desenho aberto: cansa menos em texto longo.',
    TIMES: 'A clássica de jornal e livro; ocupa menos espaço na linha.',
};
/** A família CSS de cada fonte (as quatro existem em Windows, Mac e Linux). */
export const FAMILIA_DA_FONTE = {
    ARIAL: 'Arial, Helvetica, "Liberation Sans", sans-serif',
    VERDANA: 'Verdana, Geneva, "DejaVu Sans", sans-serif',
    GEORGIA: 'Georgia, "Times New Roman", serif',
    TIMES: '"Times New Roman", Times, "Liberation Serif", serif',
};
export function fonteDoTexto(valor) {
    return FONTES_DO_TEXTO.includes(valor) ? valor : 'ARIAL';
}
export const CORPO_MINIMO_PT = 11;
export const CORPO_MAXIMO_PT = 24;
export const LARGURA_MINIMA_MM = 120;
export const LARGURA_MAXIMA_MM = 180;
/**
 * Largura especial: **ocupa a tela toda**.
 *
 * Zero, e não `null`: zero cabe num campo numérico e não se confunde com "não
escolheu"
 * (que é o padrão de 150 mm). Quem quer a página inteira — tabela larga, texto em
 * duas colunas colado de fora — escolhe esta opção.
 */
export const LARGURA_DA_TELA = 0;
export const FORMATO_PADRAO = {
    fonte: 'ARIAL',
    corpoPt: 14,
    /** 150 mm: a coluna de um livro, confortável para ler na tela. */
    larguraMm: 150,
};
/** A largura pedida: um valor entre 120 e 180 mm, ou `LARGURA_DA_TELA` (0). */
function normalizarLargura(valor) {
    const numero = Math.round(Number(valor));
    if (numero === LARGURA_DA_TELA)
        return LARGURA_DA_TELA;
    if (!Number.isFinite(numero))
        return FORMATO_PADRAO.larguraMm;
    return Math.min(Math.max(numero, LARGURA_MINIMA_MM), LARGURA_MAXIMA_MM);
}
/** O formato salvo, com cada campo dentro do que a tela aceita. */
export function formatoDoTexto(formato) {
    const numero = (valor, padrao, minimo, maximo) => {
        const n = Number(valor);
        if (!Number.isFinite(n))
            return padrao;
        return Math.min(Math.max(Math.round(n), minimo), maximo);
    };
    return {
        fonte: fonteDoTexto(formato?.fonte),
        corpoPt: numero(formato?.corpoPt, FORMATO_PADRAO.corpoPt, CORPO_MINIMO_PT, CORPO_MAXIMO_PT),
        larguraMm: normalizarLargura(formato?.larguraMm),
    };
}
/* ------------------------------------------------------------------ */
/* Apresentação (a leitura em corpo grande)                            */
/* ------------------------------------------------------------------ */
/**
 * Os degraus do corpo na tela de apresentação.
 *
 * Não é zoom de navegador: é a mesma página, com o texto maior. O passo é grande
 * de propósito — quem abre a apresentação quer **ver melhor**, e uma escada de
 * passos pequenos daria a impressão de que o botão não funciona.
 *
 * O degrau inicial é 38 pt, e não 30: a apresentação ocupa a tela toda, e em tela
 * larga o corpo de 30 pt fica pequeno para quem lê de longe ou enxerga pouco.
 */
export const CORPOS_DA_APRESENTACAO = [20, 26, 32, 38, 46, 56];
export const CORPO_DA_APRESENTACAO_PADRAO = 38;
/** O degrau seguinte (ou o mesmo, se já está no último). */
export function aumentarCorpo(corpo) {
    const proximo = CORPOS_DA_APRESENTACAO.find((valor) => valor > corpo);
    return proximo ?? CORPOS_DA_APRESENTACAO[CORPOS_DA_APRESENTACAO.length - 1];
}
/** O degrau anterior (ou o mesmo, se já está no primeiro). */
export function diminuirCorpo(corpo) {
    const anteriores = CORPOS_DA_APRESENTACAO.filter((valor) => valor < corpo);
    return anteriores[anteriores.length - 1] ?? CORPOS_DA_APRESENTACAO[0];
}
/* ------------------------------------------------------------------ */
/* Limites e estado inicial                                            */
/* ------------------------------------------------------------------ */
/** O teto do texto livre, em caracteres (≈ 400 páginas de livro). */
export const LIMITE_DE_CARACTERES = 400_000;
/** Quantos textos cada conta pode guardar. */
export const MAXIMO_DE_TEXTOS = 200;
/** O conteúdo de um texto novo: um parágrafo vazio, para o cursor ter onde nascer. */
export const CONTEUDO_INICIAL = '<p><br></p>';
/**
 * Prévia em texto puro, para a lista de textos.
 *
 * Sai do HTML sem parser (é o servidor que chama, e ele não tem DOM): tira tags,
 * resolve as entidades que o editor escreve (`&nbsp;`, `&amp;`...) e corta.
 */
export function previaDoTexto(html, limite = 180) {
    const semTag = html
        .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/(p|div|h[1-6]|li|tr|blockquote)>/gi, ' ')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/\s+/g, ' ')
        .trim();
    return semTag.length > limite ? `${semTag.slice(0, limite).trimEnd()}…` : semTag;
}
/**
 * A contagem que a barra mostra: palavras do texto, sem tags.
 *
 * A pessoa que escreve uma carta quer saber "quantas palavras tem", e é o número
 * que ela entende melhor do que caracteres.
 */
export function contarPalavras(html) {
    const texto = previaDoTexto(html, Number.MAX_SAFE_INTEGER);
    return texto ? texto.split(/\s+/).filter(Boolean).length : 0;
}
//# sourceMappingURL=texto-livre.js.map